import React, { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import RowDivider from '../../../common/components/RowDivider'
import Select from '../../../common/components/Select'
import { ChargePointExternalServerWidget } from '../../../common/features/chargepoint-external-server-widget'
import { notifyError, notifySuccess } from '../../../common/features/notificationsManager/Notify'
import {
    ChargePoint,
    useApiCustomerGetAllQuery,
    useChargePointDisableOcppMutation,
    useChargePointEnableOcppMutation,
    useChargePointGetStatusIntervalsQuery,
    useChargePointUpdateMutation,
    useCountrySettingsGetAllCountrySettingsQuery,
    useExternalServerGetAllQuery,
    useProductGetHardwareversionsQuery,
} from '../../../common/redux/api/web-api'
import { SContainer, SSubHeadings } from '../../../common/styles/Form'
import { ApiCustomerOptions } from '../../../common/types/ApiCustomerOptions'
import { transformCustomerData } from '../../../common/utils/chargePointUtils'
import { isSupportDomain } from '../../../lib/currentDomain'
import isAura from '../../../lib/isAura'
import useText from '../../../common/hooks/useText'
import Text from '../../../ui/Text'
import isHalo from '../../../lib/isHalo'

import { SCheckbox, SFormRow } from './charge-point-capi-details-widget.style'

interface IComponentProps {
    chargePoint: ChargePoint
}

const ChargePointDetailsSupportCapiInputs = ({ chargePoint: initialChargePoint }: IComponentProps) => {
    const text = useText()
    const [callbackEnabled, setCallbackEnabled] = useState(
        !!initialChargePoint.additionalInfo?.callbackEnabledApiCustomerId
    )
    const [apiCustomerOptions, setApiCustomerOptions] = useState<ApiCustomerOptions[] | undefined>([])
    const [chargePoint, setChargePoint] = useState<ChargePoint>(JSON.parse(JSON.stringify(initialChargePoint)))
    const [ocppEnabled, setOcppEnabled] = useState<boolean>(chargePoint.ocppEnabled ?? false)

    const {
        setFocus,
        register,
        setValue,
        formState: { errors },
    } = useFormContext()

    const [chargePointDisableOcpp, { isLoading: disableLoading }] = useChargePointDisableOcppMutation()
    const [chargePointEnableOcpp, { isLoading: enableLoading }] = useChargePointEnableOcppMutation()
    const { data: statusIntervalsOptions } = useChargePointGetStatusIntervalsQuery()
    const { data: countriesSettingsOptions } = useCountrySettingsGetAllCountrySettingsQuery()
    const { data: externalServersOptions } = useExternalServerGetAllQuery(undefined, { skip: !isSupportDomain })
    const { data: apiCustomerOptionsData } = useApiCustomerGetAllQuery({
        enabled: true,
        callbackEnabled: true,
    })
    const [updateChargePoint] = useChargePointUpdateMutation()

    useEffect(() => {
        if (apiCustomerOptionsData) {
            const apiCustomerOptions = transformCustomerData(apiCustomerOptionsData)

            setApiCustomerOptions(apiCustomerOptions)
        }
    }, [apiCustomerOptionsData])

    useEffect(() => {
        if (!callbackEnabled) {
            setValue('callbackEnabledApiCustomerId', null, {
                shouldValidate: true,
                shouldDirty: true,
            })
        }

        const easyparkCustomer = apiCustomerOptions?.find((x) => x.label?.toLowerCase().includes('easypark'))
        if (easyparkCustomer) {
            setChargePoint((prevChargePoint) => ({
                ...prevChargePoint,
                additionalInfo: {
                    ...prevChargePoint.additionalInfo,
                    callbackEnabledApiCustomerId: easyparkCustomer.value,
                },
            }))
        }
    }, [apiCustomerOptions, callbackEnabled, setValue])

    const toggleCallbackEnabled = () => {
        setCallbackEnabled(!callbackEnabled)
        if (callbackEnabled) {
            setFocus('callbackEnabledApiCustomerId')
        }
    }

    const updateOcppServer = async (option?: number) => {
        try {
            const tempChargePoint = {
                ...chargePoint,
                id: chargePoint?.id,
                additionalInfo: {
                    ...chargePoint?.additionalInfo,
                    externalServerId: option,
                },
            }
            await updateChargePoint({
                chargePoint: tempChargePoint,
            }).unwrap()
            setChargePoint(tempChargePoint)
        } catch (e) {
            notifyError(text('msgChargePointNotUpdated'))
        }
    }

    const toggleOcppAvailability = async (forceUpdate: boolean = false) => {
        try {
            if (chargePoint?.id) {
                if (chargePoint?.ocppEnabled) {
                    await chargePointDisableOcpp({
                        chargePointId: chargePoint?.id,
                        ...(forceUpdate && { forceUpdate }),
                    }).unwrap()
                    {
                        notifySuccess(text('lblProxyDisabled'))
                    }
                } else {
                    await chargePointEnableOcpp({
                        chargePointId: chargePoint?.id,
                        ...(forceUpdate && { forceUpdate }),
                    }).unwrap()
                    {
                        notifySuccess(text('lblProxyEnabled'))
                    }
                }
                setChargePoint({ ...chargePoint, ocppEnabled: !chargePoint.ocppEnabled })
                setOcppEnabled(!ocppEnabled)
            }
        } catch (error) {
            notifyError(text('lblProxyDisableEnableError'))
        }
    }

    const { data: hardwareVersionsOptions } = useProductGetHardwareversionsQuery(
        {
            productId: chargePoint.productId,
        },
        {
            skip: !chargePoint.productId,
        }
    )

    const hwVersion = hardwareVersionsOptions?.map((obj) => {
        return { key: obj.id, value: obj.description }
    })

    const countrySettings = countriesSettingsOptions?.map((obj) => {
        return { key: obj.id, value: obj.countryName }
    })

    const statusIntervals = statusIntervalsOptions?.map((obj) => {
        return { key: Number(obj.id), value: obj.description }
    })

    const apiCustomer = apiCustomerOptionsData?.map((obj) => {
        return { key: obj.id, value: `${obj.name} : ${obj.callbackBaseUrl}` }
    })

    return (
        <>
            <SFormRow>
                <label htmlFor="hardwareVersionId" data-testid="hw-field">
                    <Text code="lblHardwareVersion" />
                </label>
                <Select
                    id="hardwareVersionId"
                    options={hwVersion}
                    enableDefaultOption
                    {...(hardwareVersionsOptions && {
                        ...register('hardwareVersionId', { valueAsNumber: true }),
                    })}
                />
            </SFormRow>
            <SFormRow>
                <label htmlFor="countrySettingsId" data-testid="country-field">
                    <Text code="lblCountry" />
                </label>
                <Select
                    id="countrySettingsId"
                    options={countrySettings}
                    enableDefaultOption
                    {...(countrySettings && {
                        ...register('countrySettingsId', { valueAsNumber: true }),
                    })}
                />
            </SFormRow>
            <SFormRow>
                <label htmlFor="statusIntervalId" data-testid="interval-field">
                    <Text code="lblStatusUpdateInterval" />
                </label>
                <Select
                    id="statusIntervalId"
                    options={statusIntervals}
                    {...(statusIntervals && { ...register('statusIntervalId') })}
                    disabled={isHalo(chargePoint)}
                />
            </SFormRow>
            <RowDivider />

            <ChargePointExternalServerWidget
                enableLoading={enableLoading}
                disableLoading={disableLoading}
                disabledToggleCpms={!chargePoint.additionalInfo?.externalServerId}
                chargePoint={chargePoint}
                disableCpms={chargePoint.ocppEnabled}
                externalServersOptions={externalServersOptions}
                updateExternalServer={updateOcppServer}
                toggleOcppAvailability={toggleOcppAvailability}
            />

            <RowDivider />
            <SContainer>
                <SSubHeadings data-testid="OCPP-toggle">
                    <Text code="lblChargerSettingsLink" />
                </SSubHeadings>
            </SContainer>
            <SFormRow>
                <SCheckbox htmlFor="debugEnabled">
                    <input
                        className="checkbox-black"
                        type="checkbox"
                        id="debugEnabled"
                        data-testid="debug-input"
                        {...register('debugEnabled')}
                    />
                    <span data-testid="debug-field">
                        <Text code="lblDebugEnabled" />
                    </span>
                </SCheckbox>
            </SFormRow>

            {isAura(chargePoint) && (
                <SFormRow>
                    <SCheckbox htmlFor="allowConnectCurrent">
                        <input
                            className="checkbox-black"
                            type="checkbox"
                            id="allowConnectCurrent"
                            data-testid="allowed-conn-input"
                            {...register('allowConnectCurrent')}
                        />
                        <span data-testid="allowed-conn-field">
                            <Text code="lblAllowConnectCurrentField" />
                        </span>
                    </SCheckbox>
                </SFormRow>
            )}
            <RowDivider />
            <SContainer>
                <SSubHeadings data-testid="OCPP-toggle">
                    <Text code="lblCallbackSettings" />
                </SSubHeadings>
            </SContainer>
            <SFormRow>
                <SCheckbox htmlFor="callbackEnabled">
                    <input
                        className="checkbox-black"
                        type="checkbox"
                        id="callbackEnabled"
                        data-testid="callback-input"
                        checked={callbackEnabled}
                        onClick={() => toggleCallbackEnabled()}
                    />
                    <span data-testid="callback-field">
                        <Text code="lblCallbackEnabled" />
                    </span>
                </SCheckbox>
            </SFormRow>
            {callbackEnabled && (
                <SFormRow>
                    <label htmlFor="callbackEnabledApiCustomerId" data-testid="select-customer-field">
                        <Text code="lblSelectCustomer" />
                    </label>
                    <Select
                        id="callbackEnabledApiCustomerId"
                        options={apiCustomer}
                        enableDefaultOption
                        {...(apiCustomer && {
                            ...register('callbackEnabledApiCustomerId', {
                                required: text('lblCallbackCustomerRequired'),
                            }),
                        })}
                        error={errors.callbackEnabledApiCustomerId?.message}
                    />
                </SFormRow>
            )}
        </>
    )
}

export default ChargePointDetailsSupportCapiInputs
